import { formatChartDate, sortDailyReports } from '../utils/chartDates';
// COMPLETE src/components/RealFleetMileageChart.tsx - WITH ENHANCED TOOLTIP

import React, { useState } from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Cell } from 'recharts';
import MileageDetailModal from './MileageDetailModal';

interface Props {
  data: any;
}

// ✅ NEW: Custom Tooltip with vehicle breakdown
const CustomMileageTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  const MAX_VEHICLES_SHOWN = 5;

  // Sort vehicles by mileage (highest first)
  const sortedVehicles = data.vehicles
    ? [...data.vehicles].sort((a: any, b: any) => b.mileage - a.mileage)
    : [];

  const topVehicles = sortedVehicles.slice(0, MAX_VEHICLES_SHOWN);
  const remainingCount = sortedVehicles.length - MAX_VEHICLES_SHOWN;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        color: 'text.primary',
        border: 1, borderColor: 'divider',
        borderRadius: 2,
        p: 1.5,
        boxShadow: 2,
        width: 'min(280px, calc(100vw - 40px))',
        maxWidth: 350,
        maxHeight: 400,
        overflow: 'auto',
      }}
    >
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
        {formatChartDate(data.fullDate, true)}
      </Typography>

      <Typography variant="body2" color="primary" fontWeight="600" sx={{ mb: 1 }}>
        Total: {data.mileage?.toLocaleString() || 0} km
      </Typography>

      {topVehicles.length > 0 && (
        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1, mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            {sortedVehicles.length > MAX_VEHICLES_SHOWN
              ? `Top ${MAX_VEHICLES_SHOWN} Contributors:`
              : 'Vehicle Breakdown:'}
          </Typography>
          {topVehicles.map((vehicle: any, idx: number) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                py: 0.5,
                gap: 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    display: 'block',
                    lineHeight: 1.3,
                  }}
                >
                  {vehicle.plateNumber || vehicle.imei}
                </Typography>
                {(vehicle.make || vehicle.model) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.65rem',
                      display: 'block',
                      lineHeight: 1.2,
                    }}
                  >
                    {vehicle.make} {vehicle.model}
                  </Typography>
                )}
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  whiteSpace: 'nowrap',
                  fontWeight: 500,
                  pt: 0.25,
                }}
              >
                {vehicle.mileage?.toLocaleString() || 0} km
              </Typography>
            </Box>
          ))}

          {remainingCount > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                py: 0.5,
                mt: 0.5,
                borderTop: '1px dashed #e0e0e0',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                + {remainingCount} more vehicle{remainingCount > 1 ? 's' : ''}
              </Typography>
              <Typography variant="caption" color="primary" fontWeight="600">
                Click for details
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {data.vehicleCount > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontWeight: 500 }}>
          {data.vehicleCount} vehicle{data.vehicleCount > 1 ? 's' : ''} active
        </Typography>
      )}
    </Box>
  );
};

const RealFleetMileageChart: React.FC<Props> = ({ data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDayData, setSelectedDayData] = useState<any>(null);

  if (!data || !data.fleetDaily || data.fleetDaily.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Typography color="textSecondary" variant="body2" textAlign="center">
          No mileage data available
        </Typography>
      </Box>
    );
  }

  // Format data for vertical bar chart
  const chartData = sortDailyReports<any>(data.fleetDaily).map((item: any) => ({
    day: item.date.slice(0, 10), mileage: item.totalMileage ?? 0,
    date: item.date, fullDate: item.date, vehicleCount: item.vehicleCount ?? 0,
    avgMileagePerVehicle: item.avgMileagePerVehicle ?? 0, vehicles: item.vehicles || [],
  }));
  if (!chartData.length) return <Typography color="text.secondary">No dated mileage data available</Typography>;


  // Format large numbers (e.g., 92767 → "92.8k")
  const formatMileage = (value: number) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`;
    }
    return value.toString();
  };

  // Handle bar click
  const handleChartClick = (chartData: any) => {
    if (chartData && chartData.activePayload && chartData.activePayload[0]) {
      const dayData = chartData.activePayload[0].payload;
      setSelectedDayData({
        date: dayData.fullDate,
        totalMileage: dayData.mileage,
        vehicleCount: dayData.vehicleCount,
        avgMileagePerVehicle: dayData.avgMileagePerVehicle,
        vehicles: dayData.vehicles,
      });
      setModalOpen(true);
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: 240, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>Distance (km) · {formatChartDate(chartData[0].date)} – {formatChartDate(chartData[chartData.length - 1].date)} · Select a bar for details</Typography>
      <Box sx={{ flex: 1, minHeight: 200 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          onClick={handleChartClick}
          margin={{
            top: 10,
            right: isMobile ? 10 : 20,
            left: 0,
            bottom: 8
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
          <XAxis
            dataKey="day"
            tickFormatter={value => formatChartDate(value)}
            minTickGap={16}
            interval="preserveStartEnd"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            tickFormatter={formatMileage}
            width={44}
          />
          {/* ✅ UPDATED: Use custom tooltip */}
          <Tooltip content={<CustomMileageTooltip />} />
          <Bar
            dataKey="mileage"
            radius={[8, 8, 0, 0]}
            cursor="pointer"
            maxBarSize={42}
          >
            {chartData.map((entry: any, index: number) => (
              <Cell
                key={`cell-${index}`}
                fill={theme.palette.info.main}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      </Box>

      {/* Mileage Detail Modal */}
      <MileageDetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        date={selectedDayData?.date || ''}
        data={selectedDayData}
      />
    </Box>
  );
};

export default RealFleetMileageChart;
